/**
 * 参謀くん連携の `excludedPictureRequestDetailIds` 計算ロジック。
 *
 * 取得済不動産登記画面（acquireBookStatus）の FeedGrid から呼ばれる。
 *
 * - フィルタにより非表示の行も「選択されていない」扱いで除外対象に含める必要があるため、
 *   filteredRows ではなく convertedRows（全行）を渡す前提。
 * - mergeRowsByProperty で複数明細（所有者事項 / 全部事項）を 1 行に集約しているため、
 *   両方の picture_request_detail.id を除外リストに含める。
 * - -1 はその種別の明細が存在しない場合のデフォルト値なので除外。
 * - 取得失敗（"FAILED"）や取得中（"PROCESSING"）の明細は分析対象として無効なので、
 *   除外リストにもカウントにも含めない。
 * - 他ユーザー取得の明細は連携対象に含まれない。merge 後の `isOtherUserResource`
 *   は行全体の AND 集約で「自分の登記が 1 件でもあれば false」になる仕様のため、
 *   行全体フラグでは混在ケース（自分の全部事項 + 他ユーザーの所有者事項）を捌けない。
 *   そのため per-detail の `ownerInfoIsOtherUser` / `allMattersIsOtherUser` を見る。
 */
export interface RowWithDetailIds {
  id: number;
  ownerInfoDetailId: number;
  /**
   * 所有者事項の取得状態。`AcquireBookStatusDataGridRow.ownerInfo` と同形式:
   * 取得成功時は URI 文字列、未取得は ""、失敗は "FAILED"、取得中は "PROCESSING"。
   */
  ownerInfo: string;
  /** ownerInfoDetailId が他ユーザー所有の場合 true。連携対象から除外する。 */
  ownerInfoIsOtherUser: boolean;
  allMattersDetailId: number;
  /** 全部事項の取得状態。フォーマットは `ownerInfo` と同じ。 */
  allMattersInfo: string;
  /** allMattersDetailId が他ユーザー所有の場合 true。連携対象から除外する。 */
  allMattersIsOtherUser: boolean;
}

/**
 * MUI の `GridSelectionModel` は string | number を許容するため、
 * 比較前に数値へ正規化する。数値変換できないものは未選択扱いとして無視。
 * これを怠ると `selectedIdsSet.has(row.id)` が常に false となり、
 * 選択行までもが除外リストに含まれてしまう。
 */
const toNumericIdSet = (ids: ReadonlyArray<number | string>): Set<number> => {
  const set = new Set<number>();
  for (const id of ids) {
    const numId = typeof id === "string" ? Number(id) : id;
    if (Number.isFinite(numId)) {
      set.add(numId);
    }
  }
  return set;
};

/**
 * 取得状態文字列が「解析対象として有効」かを判定する。
 *
 * - URI (string)        : 取得成功 → 有効
 * - ""                  : 該当行に当該種別の picture_request_detail が存在しない → 無効
 * - "FAILED"            : 取得失敗 → 無効
 * - "PROCESSING"        : 取得中 → 無効
 */
const isAcquireSucceeded = (info: string | undefined): boolean => {
  return (
    info !== undefined &&
    info !== "" &&
    info !== "FAILED" &&
    info !== "PROCESSING"
  );
};

/**
 * 行内で「分析対象として有効な」明細 ID を集める。
 * 明細ごとに以下のいずれかに該当する場合は含めない:
 * - 他ユーザー所有の明細（per-detail フラグが true）
 * - detail ID が -1（その種別の明細なし）
 * - 対応する取得状態が succeeded 以外
 */
const collectValidDetailIds = (row: RowWithDetailIds): number[] => {
  const ids: number[] = [];
  if (
    row.ownerInfoDetailId !== -1 &&
    isAcquireSucceeded(row.ownerInfo) &&
    !row.ownerInfoIsOtherUser
  ) {
    ids.push(row.ownerInfoDetailId);
  }
  if (
    row.allMattersDetailId !== -1 &&
    isAcquireSucceeded(row.allMattersInfo) &&
    !row.allMattersIsOtherUser
  ) {
    ids.push(row.allMattersDetailId);
  }
  return ids;
};

export const calcExcludedDetailIds = (
  rows: readonly RowWithDetailIds[],
  selectedIds: ReadonlyArray<number | string>
): number[] => {
  const selectedIdsSet = toNumericIdSet(selectedIds);
  return rows
    .filter((row) => !selectedIdsSet.has(row.id))
    .flatMap(collectValidDetailIds);
};

/**
 * 選択行のうち、指定種別の「分析対象として有効な」明細 ID を実際に持つ行の件数を返す。
 *
 * FeedGrid の参謀くん連携ボタンの Disable 判定で「選択行数」を上限と比較する際、
 * 図面のみで所有者事項 / 全部事項の明細を持たない行、あるいは当該種別の取得が
 * FAILED / PROCESSING の行を数えてしまうと、実際に送信される件数と乖離し
 * 誤判定の原因になる。
 *
 * @param target - "owner" なら ownerInfoDetailId、"allMatters" なら allMattersDetailId を見る
 */
export const countSelectedRowsWithDetailId = (
  rows: readonly RowWithDetailIds[],
  selectedIds: ReadonlyArray<number | string>,
  target: "owner" | "allMatters"
): number => {
  const selectedIdsSet = toNumericIdSet(selectedIds);
  return rows.filter((row) => {
    if (!selectedIdsSet.has(row.id)) return false;
    if (target === "owner") {
      return (
        row.ownerInfoDetailId !== -1 &&
        isAcquireSucceeded(row.ownerInfo) &&
        !row.ownerInfoIsOtherUser
      );
    }
    return (
      row.allMattersDetailId !== -1 &&
      isAcquireSucceeded(row.allMattersInfo) &&
      !row.allMattersIsOtherUser
    );
  }).length;
};
